import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProfileBySlug } from '../lib/auth.js';
import { fetchLikesForUserId } from '../lib/likes.js';
import { useAuth } from '../lib/auth.js';
import { tmdb } from '../lib/tmdb.js';
import services from '../config/services.json';
import ForYouShelf from '../components/ForYouShelf.jsx';
import BecauseYouLovedShelf from '../components/BecauseYouLovedShelf.jsx';
import PosterCard from '../components/PosterCard.jsx';
import MovieModal from '../components/MovieModal.jsx';
import { formatMemberNumber } from '../lib/member.js';

export default function Profile() {
  const { slug } = useParams();
  const { user, profile: myProfile } = useAuth();

  const [profile, setProfile]       = useState(null);
  const [notFound, setNotFound]     = useState(false);
  const [lovedItems, setLovedItems] = useState([]); // [{ tmdb_id, media_type }]
  const [lovedDetails, setLovedDetails] = useState([]);
  const [openRef, setOpenRef]       = useState(null);

  const providerIds = useMemo(
    () => services.services.filter((s) => s.active).map((s) => s.tmdbProviderId).join('|'),
    []
  );

  // Resolve slug → profile.
  useEffect(() => {
    let alive = true;
    setProfile(null); setNotFound(false); setLovedItems([]); setLovedDetails([]);
    getProfileBySlug(slug).then((p) => {
      if (!alive) return;
      if (!p) setNotFound(true);
      else setProfile(p);
    });
    return () => { alive = false; };
  }, [slug]);

  // Fetch this user's loves (movies + TV).
  useEffect(() => {
    if (!profile) return;
    let alive = true;
    fetchLikesForUserId(profile.user_id).then((rows) => {
      if (!alive) return;
      setLovedItems(rows.map((r) => ({ tmdb_id: r.tmdb_id, media_type: r.media_type || 'movie' })));
    });
    return () => { alive = false; };
  }, [profile?.user_id]);

  // Hydrate the most recent ~12 loved titles for the "Recently loved" wall.
  useEffect(() => {
    if (!lovedItems.length) { setLovedDetails([]); return; }
    let alive = true;
    Promise.all(lovedItems.slice(0, 12).map((it) =>
      tmdb.media(it.tmdb_id, it.media_type).catch(() => null)
    )).then((list) => alive && setLovedDetails(list.filter(Boolean)));
    return () => { alive = false; };
  }, [lovedItems.map((i) => `${i.media_type}:${i.tmdb_id}`).join(',')]);

  if (notFound) {
    return (
      <div className="hb-empty hb-404">
        <h1>No such member.</h1>
        <p>That video club card isn't in our system.</p>
        <Link to="/" className="hb-chip is-active">← Back to The Floor</Link>
      </div>
    );
  }

  if (!profile) return <div className="hb-empty">Looking up that member…</div>;

  const isOwner = user && profile.user_id === user.id;

  return (
    <div className="hb-profile">
      <div className="hb-profile__header">
        <div>
          <div className="hb-profile__label">
            Member {formatMemberNumber(profile.member_number)}
          </div>
          <h1 className="hb-profile__name">{profile.display_name}</h1>
          <div className="hb-profile__slug">/u/{profile.slug}</div>
        </div>
        {isOwner ? (
          <div className="hb-profile__badge">This is you</div>
        ) : (
          <div className="hb-profile__badge hb-profile__badge--visitor">Viewing as guest</div>
        )}
      </div>

      <ForYouShelf
        providerIds={providerIds}
        onPosterClick={(m) => setOpenRef({ tmdb_id: m.id, media_type: m.media_type || 'movie' })}
        seedItemsOverride={lovedItems}
        ownerName={profile.display_name}
      />

      {/* Per-seed "Because you loved X" shelves — up to 4 most recent loves. */}
      {lovedDetails.slice(0, 4).map((d) => (
        <BecauseYouLovedShelf
          key={`${d.media_type}:${d.id}`}
          seed={{ tmdb_id: d.id, media_type: d.media_type || 'movie' }}
          seedTitle={d.title || d.name || 'this'}
          providerIds={providerIds}
          onPosterClick={(m) => setOpenRef({ tmdb_id: m.id, media_type: m.media_type || 'movie' })}
        />
      ))}

      <section className="hb-shelf">
        <div className="hb-shelf__sign">
          <h2 className="hb-shelf__title">Recently Loved</h2>
          <p className="hb-shelf__subtitle">
            What {isOwner ? 'you have' : profile.display_name + ' has'} hearted lately
          </p>
        </div>
        <div className="hb-shelf__rail">
          {lovedItems.length === 0 ? (
            <div className="hb-shelf__empty">
              {isOwner
                ? 'No loves yet — heart any movie or show to add it here.'
                : `${profile.display_name} hasn’t loved anything yet.`}
            </div>
          ) : lovedDetails.length === 0 ? (
            Array.from({ length: 6 }).map((_, i) => <div key={i} className="hb-poster hb-poster--skeleton" />)
          ) : (
            lovedDetails.map((m) => (
              <PosterCard
                key={`${m.media_type}:${m.id}`}
                movie={m}
                onClick={() => setOpenRef({ tmdb_id: m.id, media_type: m.media_type })}
              />
            ))
          )}
        </div>
      </section>

      {openRef && <MovieModal movieId={openRef} onClose={() => setOpenRef(null)} />}
    </div>
  );
}
